import { useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AISendButton from "./AISendButton";

export const ASSISTANT_SOURCE_MAX_BYTES = 100 * 1024 * 1024;
export const ASSISTANT_SOURCE_ACCEPT = [
  ".pdf",
  ".ppt",
  ".pptx",
  ".txt",
  ".docx",
  "application/pdf",
  "text/plain",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
].join(",");

export function validateAssistantSourceFile(file) {
  if (!file) return "Choose a file.";
  const ext = String(file.name || "").split(".").pop()?.toLowerCase() || "";
  if (ext === "doc") return "Save the Word file as .docx, then upload that.";
  if (!["pdf", "ppt", "pptx", "txt", "docx"].includes(ext)) {
    return "Upload a PDF, PowerPoint (.ppt or .pptx), text, or Word (.docx) file.";
  }
  if (file.size > ASSISTANT_SOURCE_MAX_BYTES) return "File must be 100 MB or smaller.";
  return "";
}

function formatBytes(size) {
  const n = Number(size) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${n} B`;
}

export default function AIChatInput({
  value,
  onChange,
  onSend,
  disabled,
  sending,
  placeholder,
  attachment,
  onAttach,
  onClearAttachment,
  dragging,
  onDragState,
}) {
  const inputRef = useRef(null);
  const [localDrag, setLocalDrag] = useState(false);
  const attachmentReady = attachment?.status === "ready";
  const attachmentBusy = attachment?.status === "uploading";
  const canSend =
    (Boolean(String(value || "").trim()) || attachmentReady) &&
    !disabled &&
    !sending &&
    !attachmentBusy;

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (canSend) onSend();
    }
  };

  const takeFile = (file) => {
    if (!file || disabled || sending) return;
    onAttach?.(file);
  };

  const dragActive = dragging || localDrag;

  return (
    <Box
      onDragEnter={(event) => {
        event.preventDefault();
        setLocalDrag(true);
        onDragState?.(true);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setLocalDrag(true);
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setLocalDrag(false);
        onDragState?.(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        setLocalDrag(false);
        onDragState?.(false);
        takeFile(event.dataTransfer?.files?.[0]);
      }}
      sx={{
        px: 2,
        pt: 1.25,
        pb: 1.5,
        borderTop: "1px solid rgba(148,163,184,0.25)",
        bgcolor: dragActive ? "#eff6ff" : "#ffffff",
      }}
    >
      {attachment ? (
        <Box
          sx={{
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.25,
            py: 0.75,
            borderRadius: 2,
            bgcolor: attachment.status === "error" ? "#fef2f2" : "#f8fafc",
            border: "1px solid",
            borderColor: attachment.status === "error" ? "#fecaca" : "rgba(148,163,184,0.35)",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 650, color: "#0f172a" }} noWrap>
              {attachment.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: attachment.status === "error" ? "#b91c1c" : "#64748b" }}>
              {attachment.status === "uploading"
                ? "Reading file…"
                : attachment.status === "error"
                  ? attachment.error || "Upload failed"
                  : `${formatBytes(attachment.size)}${
                      attachment.imageCount
                        ? ` · ${attachment.imageCount} figure${attachment.imageCount === 1 ? "" : "s"} saved`
                        : ""
                    }`}
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Remove file"
            onClick={onClearAttachment}
            disabled={attachmentBusy}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
        <IconButton
          aria-label="Attach a file"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || sending || attachmentBusy}
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            color: "#1d4ed8",
            bgcolor: "#eff6ff",
            border: "1px solid rgba(37,99,235,0.18)",
          }}
        >
          <AttachFileOutlinedIcon fontSize="small" />
        </IconButton>
        <input
          ref={inputRef}
          type="file"
          accept={ASSISTANT_SOURCE_ACCEPT}
          hidden
          onChange={(event) => {
            takeFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        <TextField
          fullWidth
          multiline
          maxRows={5}
          minRows={1}
          placeholder={
            dragActive
              ? "Drop a PDF, PowerPoint, text, or Word file"
              : placeholder || "Ask Microlab AI… or attach a file (Enter to send)"
          }
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || sending}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              bgcolor: "#f8fafc",
              fontSize: 14,
              alignItems: "flex-end",
            },
          }}
        />
        <AISendButton disabled={!canSend} loading={sending || attachmentBusy} onClick={onSend} />
      </Box>
    </Box>
  );
}

AIChatInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  sending: PropTypes.bool,
  placeholder: PropTypes.string,
  attachment: PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
    status: PropTypes.string,
    error: PropTypes.string,
    imageCount: PropTypes.number,
  }),
  onAttach: PropTypes.func,
  onClearAttachment: PropTypes.func,
  dragging: PropTypes.bool,
  onDragState: PropTypes.func,
};
